/**
 * Scope dial — semantic "radius rings" around an attention anchor. The dial
 * (⌃⌥↑/↓) steps OUT/IN through these rings; each ring is a *target set*, not a
 * zoom percentage, and the operator frames it with its normal gating.
 *
 *   ring 1 — the node itself
 *   ring 2 — the node + its 1-hop neighbours (dependents/dependencies)
 *   ring 3 — the space that contains the node
 *   ring 4 — that space's parent (clamped at the board root)
 *
 * Neighbours come from the dependency EDGES (board-key domain), not the engine
 * graph's id index — most modules carry no explicit `#id`, so an id-keyed lookup
 * would silently return nothing and collapse ring 2 back to ring 1.
 */
export const MIN_RING = 1;
export const MAX_RING = 4;

export interface RingContext {
  /** 1-hop neighbours of a board id (undirected), board-key domain. */
  neighbors(id: string): readonly string[];
  /** Focal path of the space that directly contains a board id, or null. */
  focalPathOf(id: string): readonly string[] | null;
}

export type RingTarget =
  | { readonly ids: readonly string[] }
  | { readonly spacePath: readonly string[] };

export function clampRing(ring: number): number {
  return Math.max(MIN_RING, Math.min(MAX_RING, Math.round(ring)));
}

/**
 * The target for `ring` around `anchorId`. Returns null when the anchor can't
 * be resolved (unknown id / lazy source not materialised).
 */
export function ringTargets(
  ring: number,
  anchorId: string,
  ctx: RingContext,
): RingTarget | null {
  const path = ctx.focalPathOf(anchorId);
  if (!path) return null;
  switch (clampRing(ring)) {
    case 1:
      return { ids: [anchorId] };
    case 2: {
      const seen = new Set<string>([anchorId]);
      const ids: string[] = [anchorId];
      for (const n of ctx.neighbors(anchorId)) {
        if (!seen.has(n)) {
          seen.add(n);
          ids.push(n);
        }
      }
      return { ids };
    }
    case 3:
      return { spacePath: path };
    default:
      // ring 4 — the containing space's parent; clamp at the root (a top-level
      // node's space has no parent, so ring 4 == ring 3 there).
      return { spacePath: path.length > 1 ? path.slice(0, path.length - 1) : path };
  }
}
